// File: frontend/src/pages/entry/EntryPermalinkPage.tsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchEntryLocation } from "../../services/entryLocationService";
import { usePreferences } from "../../context/PreferencesContext";
import { ENTRIES_PER_PAGE } from "../../constants";
import { SpinnerLoading } from "../../utils";
import { FOCUS_PARAM } from "../../hooks/useUrlPage";

/**
 * Route: /entry/{id}
 *
 * The stable permalink of a single tanım. It carries nothing but the entry id,
 * which is what makes it survive everything a topic-relative link does not:
 *
 *   - the topic being renamed (its slug changes)
 *   - entries being written before it (its page number changes)
 *   - the reader having a different "entries per page" preference than the
 *     sharer (the same entry sits on a different page for them)
 *   - the reader hiding rookie entries (fewer entries precede it)
 *
 * All of that is resolved here, at open time, against the CURRENT reader's own
 * preferences. The backend answers with the canonical topic path and the page
 * number, and this page replaces itself with that address plus the "#entry-{id}"
 * anchor, so the reader lands on the entry and the back button does not bounce
 * them through this intermediate route.
 */
export const EntryPermalinkPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { preferences } = usePreferences();

  const [notFound, setNotFound] = useState(false);

  // Same two viewer settings the entry lists themselves are rendered with, so
  // the resolved page number matches the page the reader will actually see.
  const entriesPerPage = preferences?.entriesPerPage ?? ENTRIES_PER_PAGE;
  const showRookieEntries = preferences?.showRookieEntries !== false;

  useEffect(() => {
    const entryId = Number(id);
    if (!Number.isInteger(entryId) || entryId <= 0) {
      setNotFound(true);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      try {
        const location = await fetchEntryLocation(entryId, entriesPerPage, showRookieEntries);
        if (cancelled) return;

        // Hand off to the topic page in focused mode. The address bar ends up
        // showing a readable topic URL ("/gundem/futbol--125?tanim=747")
        // rather than this opaque id route, and the topic page renders the one
        // shared tanım framed by "{n} entry daha" bars instead of dropping the
        // reader into the middle of a page and hoping an anchor scroll lands.
        navigate(`${location.path}?${FOCUS_PARAM}=${location.entryId}`, { replace: true });
      } catch {
        // Deleted, never existed, or the backend is unreachable — either way
        // there is no page to send the reader to.
        if (!cancelled) setNotFound(true);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [id, entriesPerPage, showRookieEntries, navigate]);

  if (notFound) {
    return (
      <p className="no-entries-message">
        {t("entry_permalink_not_found", "Aradığınız tanım bulunamadı. Silinmiş olabilir.")}
      </p>
    );
  }

  return <SpinnerLoading />;
};

export default EntryPermalinkPage;